import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery
} from '@material-ui/core'
import { makeStyles, useTheme, fade, darken } from '@material-ui/core/styles'
import ArrowBackIcon from '@material-ui/icons/ArrowBackIos'
import PhotoCamera from '@material-ui/icons/PhotoCamera'
import RefreshIcon from '@material-ui/icons/Refresh'

import {
  fetchScopedCentres,
  fetchChecklistBoard,
  getChecklistRunDetail,
  submitChecklistRun,
  uploadAndGetFile
} from 'api'
import constants from 'app/constants'
import { uploadPresets } from 'app/config'
import { showToast } from 'app/helpers'
import { useOpsCentre } from 'app/hooks/useOpsCentre'

import ChecklistHistory from './ChecklistHistory'

// NOTE: reuses an existing working Cloudinary image preset as a stopgap.
// A dedicated `checklist_photos` preset should be created in Cloudinary and
// wired into config.js `uploadPresets` before go-live.
const CHECKLIST_UPLOAD_PRESET = uploadPresets[constants.OPENBOOK]

// Shrink a phone photo before upload (max 1280px, JPEG) so uploads are fast.
// Uses an object URL (NOT readAsDataURL) and frees the canvas/image right after
// so several full-res phone photos in a row don't pile up in memory — that spike
// is what made the mobile browser reload the tab and lose the half-filled form.
// Falls back to the original file if anything goes wrong.
const compressImage = (file, maxDim = 1280, quality = 0.72) =>
  new Promise((resolve) => {
    if (!file || !file.type || file.type.indexOf('image/') !== 0) {
      resolve(file)
      return
    }
    const url = URL.createObjectURL(file)
    const img = new Image()
    const cleanup = () => {
      try { URL.revokeObjectURL(url) } catch (e) { /* ignore */ }
    }
    img.onload = () => {
      let canvas
      try {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height))
        const w = Math.round(img.width * scale)
        const h = Math.round(img.height * scale)
        canvas = document.createElement('canvas')
        canvas.width = w
        canvas.height = h
        canvas.getContext('2d').drawImage(img, 0, 0, w, h)
        canvas.toBlob((blob) => {
          cleanup()
          // Release the bitmap + canvas so memory is reclaimed immediately.
          canvas.width = 0
          canvas.height = 0
          img.src = ''
          resolve(blob || file)
        }, 'image/jpeg', quality)
      } catch (e) {
        cleanup()
        resolve(file)
      }
    }
    img.onerror = () => {
      cleanup()
      resolve(file)
    }
    img.src = url
  })

const extractRows = (data) =>
  (data && (data.results || data.data)) || (Array.isArray(data) ? data : [])

// Local draft key so a mobile tab reload mid-fill can be recovered on reopen.
const draftKey = (uuid) => `checklist_draft_${uuid}`

// Cadence display: which section a run belongs to, and its lead icon.
const GROUPS = [
  { key: 'today', title: 'Today' },
  { key: 'week', title: 'This week' },
  { key: 'month', title: 'This month' }
]

const groupForRun = (run) => {
  const freq = run.template ? run.template.frequency : 'daily'
  if (freq === 'weekly') return 'week'
  if (freq === 'monthly') return 'month'
  return 'today'
}

const iconForRun = (run) => {
  if (run.round === 'morning') return '🌅'
  if (run.round === 'end_of_day') return '🌙'
  const freq = run.template && run.template.frequency
  if (freq === 'weekly') return '📅'
  if (freq === 'monthly') return '🗓️'
  return '📋'
}

const pctOf = (done, total) => (total ? Math.round((done / total) * 100) : 0)

const fmtTime = (iso) => {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  } catch (e) {
    return ''
  }
}

const fmtDate = (iso) => {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'short' })
  } catch (e) {
    return ''
  }
}

// 'Tue, 28 Jul' — the due day+date for weekly/monthly checklists (a 'YYYY-MM-DD'
// due_date, parsed at local midnight so the weekday doesn't drift a day).
const fmtDueDate = (iso) => {
  if (!iso) return ''
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' })
  } catch (e) {
    return ''
  }
}

// Derived display status: the API status is pending/completed/overdue; we split
// pending into To-do vs In-progress by whether any item is already ticked.
const runDisplay = (run) => {
  if (run.status === 'completed') return { key: 'done', label: 'Done' }
  if (run.status === 'overdue') return { key: 'over', label: 'Overdue' }
  if ((run.done_items || 0) > 0) return { key: 'prog', label: 'In progress' }
  return { key: 'todo', label: 'To do' }
}

const useStyles = makeStyles((theme) => {
  const accent = theme.palette.primary.main
  const accentSoft = fade(accent, 0.12)
  const accentInk = darken(accent, 0.3)
  const done = theme.palette.text.success || '#3f9d5b'
  const doneSoft = fade(done, 0.15)
  const crit = theme.palette.error.main
  const critSoft = fade(crit, 0.15)
  const surface = theme.palette.background.default
  const isDark = theme.palette.type === 'dark'
  const surface2 = isDark ? 'rgba(255,255,255,0.06)' : '#f6f5f3'
  const line = theme.palette.divider
  const lineStrong = fade(theme.palette.text.primary, 0.24)
  const muted = fade(theme.palette.text.primary, 0.6)
  const faint = fade(theme.palette.text.primary, 0.4)
  const shadow = '0 1px 2px rgba(0,0,0,.04), 0 8px 16px rgba(0,0,0,.05)'

  return {
    root: { padding: theme.spacing(2), maxWidth: 640, margin: '0 auto' },

    header: { marginBottom: theme.spacing(2) },
    headerTop: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: theme.spacing(1), flexWrap: 'wrap'
    },
    title: { fontWeight: 700, letterSpacing: '-0.01em' },
    headerActions: { display: 'flex', alignItems: 'center', gap: theme.spacing(1), flexWrap: 'wrap' },
    modeToggle: {
      display: 'inline-flex', background: surface2, border: `1px solid ${line}`,
      borderRadius: 999, padding: 2
    },
    modeBtn: {
      border: 'none', background: 'transparent', font: 'inherit', color: muted,
      fontWeight: 600, fontSize: 13, padding: theme.spacing(0.5, 1.75), borderRadius: 999, cursor: 'pointer'
    },
    modeBtnActive: { background: surface, color: accent, boxShadow: shadow },
    centreSelect: {
      minWidth: 150,
      background: surface2, border: `1px solid ${line}`, borderRadius: 999,
      padding: theme.spacing(0.25, 1),
      '&:before, &:after': { display: 'none' },
      '& .MuiSelect-select': { paddingRight: 24, fontWeight: 600, fontSize: 13 },
      '& .MuiSelect-select:focus': { background: 'transparent' }
    },
    refreshBtn: { color: accent },

    daystrip: {
      marginTop: theme.spacing(1.5),
      display: 'flex', alignItems: 'center', gap: theme.spacing(1.5),
      background: accentSoft, border: `1px solid ${fade(accent, 0.24)}`,
      borderRadius: 12, padding: theme.spacing(1.25, 1.5)
    },
    dsBig: { fontWeight: 600, fontSize: 14.5, lineHeight: 1.2 },
    dsSub: { color: muted, fontSize: 12 },

    groupLabel: {
      fontSize: 12.5, color: muted, fontWeight: 600, margin: theme.spacing(2.25, 0.25, 1.25)
    },
    firstGroupLabel: { marginTop: theme.spacing(2) },

    card: {
      background: surface, border: `1px solid ${line}`, borderRadius: 16,
      padding: theme.spacing(1.75), display: 'flex', alignItems: 'center',
      gap: theme.spacing(1.5), marginBottom: theme.spacing(1.25),
      boxShadow: shadow, cursor: 'pointer', width: '100%', textAlign: 'left',
      font: 'inherit', color: 'inherit', appearance: 'none', WebkitAppearance: 'none',
      transition: 'transform .15s ease, box-shadow .15s ease',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 2px 6px rgba(0,0,0,.06), 0 16px 34px rgba(0,0,0,.10)' }
    },
    cardOverdue: { borderColor: fade(crit, 0.45) },
    leadIcon: {
      width: 42, height: 42, flex: '0 0 auto', borderRadius: 12, display: 'grid',
      placeItems: 'center', fontSize: 21, background: surface2, border: `1px solid ${line}`
    },
    cardBody: { flex: '1 1 auto', minWidth: 0 },
    cardName: { fontWeight: 600, fontSize: 15, letterSpacing: '-0.01em' },
    cardMeta: { color: muted, fontSize: 12.5, marginTop: 2 },
    frac: { fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: theme.palette.text.primary },

    ring: {
      '--tk': '4.5px',
      width: 40, height: 40, borderRadius: '50%', flex: '0 0 auto',
      display: 'grid', placeItems: 'center',
      background: `radial-gradient(closest-side, ${surface} calc(100% - var(--tk) - 0.5px), transparent calc(100% - var(--tk))), conic-gradient(var(--rc) calc(var(--p) * 1%), ${lineStrong} 0)`
    },
    ringBig: { width: 46, height: 46, '--tk': '5px' },
    rp: { fontSize: 10.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' },

    pill: {
      display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600,
      padding: theme.spacing(0.375, 1.125), borderRadius: 999, whiteSpace: 'nowrap', marginTop: theme.spacing(1)
    },
    dot: { width: 6, height: 6, borderRadius: '50%', background: 'currentColor' },
    pillTodo: { background: surface2, color: muted, border: `1px solid ${line}` },
    pillProg: { background: accentSoft, color: accentInk },
    pillDone: { background: doneSoft, color: done },
    pillOver: { background: critSoft, color: crit },

    empty: { textAlign: 'center', color: muted, marginTop: theme.spacing(6) },
    loadingWrap: { textAlign: 'center', marginTop: theme.spacing(4) },

    /* ---- run dialog ---- */
    dialogPaper: { borderRadius: 20, background: surface },
    runHead: {
      padding: theme.spacing(1.5, 2), borderBottom: `1px solid ${line}`,
      display: 'flex', flexDirection: 'column', gap: theme.spacing(1.25),
      position: 'sticky', top: 0, background: surface, zIndex: 2
    },
    runHeadTop: { display: 'flex', alignItems: 'center', gap: theme.spacing(1.25) },
    backBtn: {
      width: 34, height: 34, borderRadius: 10, border: `1px solid ${line}`,
      background: surface2, padding: 0
    },
    backIcon: { fontSize: 15, marginLeft: 4 },
    runName: { fontWeight: 700, fontSize: 17, letterSpacing: '-0.01em', lineHeight: 1.2 },
    runSub: { color: muted, fontSize: 12 },
    runHeadText: { flex: '1 1 auto', minWidth: 0 },
    progressBar: { height: 7, borderRadius: 99, background: line, overflow: 'hidden' },
    progressFill: { height: '100%', borderRadius: 99, background: `linear-gradient(90deg, ${darken(accent, 0.12)}, ${accent})`, transition: 'width .25s ease' },

    runContent: { padding: theme.spacing(1.5, 2, 2) },
    areaHead: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      margin: theme.spacing(2, 0.25, 1)
    },
    areaName: { fontSize: 13, color: muted, fontWeight: 600 },
    areaCount: { fontSize: 11.5, color: faint, fontVariantNumeric: 'tabular-nums', fontWeight: 600 },

    check: {
      display: 'flex', alignItems: 'flex-start', gap: theme.spacing(1.5),
      padding: theme.spacing(1.5), background: surface, border: `1px solid ${line}`,
      borderRadius: 11, marginBottom: theme.spacing(1)
    },
    checkDone: { background: fade(done, 0.08), borderColor: fade(done, 0.3) },
    box: {
      width: 24, height: 24, borderRadius: 8, border: `2px solid ${lineStrong}`,
      flex: '0 0 auto', marginTop: 1, display: 'grid', placeItems: 'center',
      color: '#fff', background: surface, cursor: 'pointer', padding: 0
    },
    checkNo: { background: fade(crit, 0.07), borderColor: fade(crit, 0.35) },
    cRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: theme.spacing(1.25) },
    yn: {
      display: 'inline-flex', flex: '0 0 auto', border: `1px solid ${line}`, borderRadius: 999,
      overflow: 'hidden', background: surface
    },
    ynBtn: {
      border: 'none', background: 'transparent', font: 'inherit', color: muted, cursor: 'pointer',
      fontWeight: 700, fontSize: 13, padding: theme.spacing(0.75, 1.75), minWidth: 52,
      '&:disabled': { cursor: 'default' },
      '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: -2 }
    },
    ynYes: { background: done, color: '#fff' },
    ynNo: { background: crit, color: '#fff' },
    noteLabel: { fontSize: 12, fontWeight: 700, color: crit },
    noteInput: {
      width: '100%',
      '& .MuiOutlinedInput-notchedOutline': { borderColor: fade(crit, 0.6) }
    },
    noteText: { fontSize: 13, color: theme.palette.text.primary, whiteSpace: 'pre-wrap' },
    cBody: { flex: '1 1 auto', minWidth: 0 },
    stackFull: { alignItems: 'stretch' },
    cText: { fontSize: 14, fontWeight: 500, lineHeight: 1.35 },
    stack: { marginTop: theme.spacing(1), display: 'flex', flexDirection: 'column', gap: theme.spacing(1), alignItems: 'flex-start' },

    tag: {
      display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5,
      fontWeight: 600, padding: theme.spacing(0.25, 0.875), borderRadius: 6
    },
    tagPhoto: { background: accentSoft, color: accentInk },
    tagData: { background: fade(theme.palette.warning ? theme.palette.warning.main : '#b5851b', 0.16), color: '#a9791b' },

    thumb: { width: '100%', maxWidth: 220, height: 92, objectFit: 'cover', borderRadius: 10, border: `1px solid ${line}` },
    dataInput: { maxWidth: 220 },

    footer: {
      position: 'sticky', bottom: 0, padding: theme.spacing(1.5, 2), borderTop: `1px solid ${line}`,
      background: surface, display: 'flex', flexDirection: 'column', gap: theme.spacing(1), zIndex: 2
    },
    hint: { fontSize: 12, color: muted, textAlign: 'center' },
    hintWarn: { color: crit, fontWeight: 700 },
    submitBtn: {
      borderRadius: 12, fontWeight: 700, textTransform: 'none', fontSize: 15, padding: theme.spacing(1.5),
      boxShadow: `0 6px 16px ${fade(accent, 0.38)}`
    }
  }
})

const Checklists = () => {
  const classes = useStyles()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'))

  const [centres, setCentres] = useState([])
  // Shared by every Centre Operations page and kept across refreshes. The
  // board needs one centre, so a saved "All centres" opens on the first.
  const [centreUuid, setCentreUuid, pickCentre] = useOpsCentre({
    allowAll: false
  })
  const [board, setBoard] = useState([])
  const [loading, setLoading] = useState(false)

  const [selected, setSelected] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [itemState, setItemState] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [mode, setMode] = useState('board')

  // load centres for the picker
  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        const data = await fetchScopedCentres()
        const rows = extractRows(data)
        if (!active) return
        setCentres(rows)
        setCentreUuid((prev) =>
          rows.some((c) => c.uuid === prev) ? prev : rows[0]?.uuid || ''
        )
      } catch (error) {
        showToast('Could not load centres', constants.ERROR)
      }
    })()
    return () => {
      active = false
    }
  }, [])

  const loadBoard = useCallback(async () => {
    if (!centreUuid) return
    setLoading(true)
    try {
      const data = await fetchChecklistBoard(centreUuid)
      setBoard(extractRows(data))
    } catch (error) {
      showToast('Could not load checklists', constants.ERROR)
    } finally {
      setLoading(false)
    }
  }, [centreUuid])

  useEffect(() => {
    loadBoard()
  }, [loadBoard])

  const sections = useMemo(() => {
    const byGroup = { today: [], week: [], month: [] }
    board.forEach((run) => {
      const g = groupForRun(run)
      if (byGroup[g]) byGroup[g].push(run)
    })
    return byGroup
  }, [board])

  const summary = useMemo(() => {
    const total = board.length
    const done = board.filter((r) => r.status === 'completed').length
    return { total, done, pct: pctOf(done, total) }
  }, [board])

  const openRun = useCallback(async (run) => {
    setSelected({ uuid: run.uuid })
    setDetailLoading(true)
    try {
      const detail = await getChecklistRunDetail(run.uuid)
      const runDetail = detail && detail.data ? detail.data : detail
      const initial = {}
      ;(runDetail.run_items || []).forEach((ri) => {
        initial[ri.uuid] = {
          // Runs from before Yes/No carry only the tick; a tick reads as Yes.
          answer: ri.answer || (ri.checked ? 'yes' : ''),
          note: ri.note || '',
          value: ri.value || '',
          photoUuid: ri.photo ? ri.photo.uuid : null,
          photoUrl: ri.photo ? ri.photo.url : null,
          uploading: false
        }
      })
      // Restore any local draft (survives a mobile tab reload) over the server
      // state — keeps uploaded photos and local ticks so a reload never wipes a
      // half-filled checklist. `uploading` is always reset.
      let restored = initial
      try {
        const raw = runDetail.status === 'completed' ? null : sessionStorage.getItem(draftKey(run.uuid))
        if (raw) {
          const saved = JSON.parse(raw) || {}
          restored = {}
          Object.keys(initial).forEach((k) => {
            restored[k] = { ...initial[k], ...(saved[k] || {}), uploading: false }
          })
        }
      } catch (e) { /* ignore */ }
      setItemState(restored)
      setSelected(runDetail)
    } catch (error) {
      showToast('Could not open this checklist', constants.ERROR)
      setSelected(null)
    } finally {
      setDetailLoading(false)
    }
  }, [])

  // Closing keeps the draft: a stray tap outside the dialog must not wipe a
  // half-filled checklist. The draft is dropped only once it is submitted.
  const closeRun = useCallback(() => {
    const started = Object.values(itemState).some((s) => s && (s.answer || s.note || s.value || s.photoUuid))
    if (started && selected && selected.status !== 'completed') {
      showToast('Saved — open the checklist again to carry on', constants.INFO)
    }
    setSelected(null)
    setItemState({})
  }, [selected, itemState])

  // Persist the in-progress checklist so a mobile reload can be recovered.
  useEffect(() => {
    if (!selected || !selected.uuid || detailLoading) return
    try {
      sessionStorage.setItem(draftKey(selected.uuid), JSON.stringify(itemState))
    } catch (e) { /* ignore quota */ }
  }, [itemState, selected, detailLoading])

  const setItem = useCallback((uuid, patch) => {
    setItemState((prev) => ({ ...prev, [uuid]: { ...prev[uuid], ...patch } }))
  }, [])

  const onPhotoSelected = useCallback(
    async (uuid, file) => {
      if (!file) return
      setItem(uuid, { uploading: true })
      // The file's own timestamp, read BEFORE compression strips the camera
      // metadata: a live capture is dated now, a gallery pick is dated when it
      // was taken. Gordon's photo audit reads it server-side.
      const photoTakenAt = file.lastModified ? new Date(file.lastModified).toISOString() : null
      try {
        const compressed = await compressImage(file)
        // Unique filename per run-item so photos never overwrite each other.
        const fileRecord = await uploadAndGetFile(compressed, {
          uploadPreset: CHECKLIST_UPLOAD_PRESET,
          public_id: `checklist_photos/${uuid}`
        })
        setItem(uuid, { photoUuid: fileRecord.uuid, photoUrl: fileRecord.url, photoTakenAt, uploading: false })
      } catch (error) {
        showToast('Photo upload failed, please try again', constants.ERROR)
        setItem(uuid, { uploading: false })
      }
    },
    [setItem]
  )

  const handleSubmit = useCallback(async () => {
    if (!selected) return
    const items = Object.entries(itemState).map(([uuid, s]) => ({
      uuid,
      checked: !!s.answer,
      answer: s.answer || '',
      note: s.note || '',
      value: s.value || '',
      photo: s.photoUuid || null,
      photo_taken_at: s.photoTakenAt || null
    }))
    setSubmitting(true)
    try {
      await submitChecklistRun(selected.uuid, items)
      try {
        sessionStorage.removeItem(draftKey(selected.uuid))
      } catch (e) { /* ignore */ }
      showToast('Checklist submitted', constants.SUCCESS)
      setSelected(null)
      setItemState({})
      loadBoard()
    } catch (error) {
      const data = error && error.response && error.response.data
      showToast((data && data.detail) || 'Could not submit checklist', constants.ERROR)
    } finally {
      setSubmitting(false)
    }
  }, [selected, itemState, loadBoard])

  const itemsByArea = useMemo(() => {
    if (!selected || !selected.run_items) return []
    const order = []
    const map = {}
    selected.run_items.forEach((ri) => {
      const area = (ri.item && ri.item.area) || 'Other'
      if (!map[area]) {
        map[area] = []
        order.push(area)
      }
      map[area].push(ri)
    })
    return order.map((area) => ({ area, items: map[area] }))
  }, [selected])

  // Live progress + which checked items still lack their required evidence.
  const liveTotal = selected && selected.run_items ? selected.run_items.length : 0
  const liveDone = useMemo(
    () => Object.values(itemState).filter((s) => s.answer).length,
    [itemState]
  )
  // Every checkpoint is mandatory: it needs a Yes or a No, a No needs a note,
  // and a photo/data item still needs its photo/reading either way (backend
  // enforces the same rule on submit).
  const missingRequired = useMemo(() => {
    if (!selected || !selected.run_items) return []
    return selected.run_items.filter((ri) => {
      const s = itemState[ri.uuid] || {}
      if (!s.answer) return true
      if (ri.item && ri.item.requires_photo && !s.photoUuid) return true
      if (ri.item && ri.item.capture_type === 'data' && !(s.value && s.value.trim())) return true
      return false
    })
  }, [selected, itemState])
  const noWithoutNote = useMemo(
    () => Object.values(itemState).filter((s) => s.answer === 'no' && !(s.note && s.note.trim())).length,
    [itemState]
  )
  const problemCount = useMemo(
    () => Object.values(itemState).filter((s) => s.answer === 'no').length,
    [itemState]
  )

  const isComplete = selected && selected.status === 'completed'
  const hasAnyCards = board.length > 0
  const livePct = pctOf(liveDone, liveTotal)

  const ringStyle = (pct, key) => {
    const rc =
      key === 'done' ? (theme.palette.text.success || '#3f9d5b')
        : key === 'over' ? theme.palette.error.main
          : theme.palette.primary.main
    return { '--p': pct, '--rc': rc }
  }

  const renderRunCard = (run) => {
    const disp = runDisplay(run)
    const pct = pctOf(run.done_items || 0, run.total_items || 0)
    const pillClass = {
      todo: classes.pillTodo, prog: classes.pillProg, done: classes.pillDone, over: classes.pillOver
    }[disp.key]
    const freq = run.template && run.template.frequency
    const dueText = freq === 'daily'
      ? (run.scheduled_for ? ` · due ${fmtTime(run.scheduled_for)}` : '')
      : (run.due_date ? ` · due ${fmtDueDate(run.due_date)}` : '')
    const meta = disp.key === 'done'
      ? `Completed${run.completed_at ? ` · ${fmtDate(run.completed_at)}` : ''}`
      : (
        <span>
          <span className={classes.frac}>{run.done_items || 0}/{run.total_items || 0}</span>
          {dueText}
        </span>
      )
    return (
      <Box
        component="button"
        key={run.uuid}
        className={`${classes.card}${disp.key === 'over' ? ` ${classes.cardOverdue}` : ''}`}
        onClick={() => openRun(run)}
      >
        <span className={classes.leadIcon}>{iconForRun(run)}</span>
        <Box className={classes.cardBody}>
          <Typography className={classes.cardName}>
            {run.template ? run.template.name : 'Checklist'}
          </Typography>
          <Typography className={classes.cardMeta} component="div">{meta}</Typography>
          <span className={`${classes.pill} ${pillClass}`}>
            {disp.key !== 'todo' && <span className={classes.dot} />}{disp.label}
          </span>
        </Box>
        <div
          className={classes.ring}
          style={ringStyle(disp.key === 'done' ? 100 : pct, disp.key)}
        >
          <span className={classes.rp} style={disp.key === 'done' ? { color: theme.palette.text.success } : {}}>
            {disp.key === 'done' ? '✓' : `${pct}%`}
          </span>
        </div>
      </Box>
    )
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.headerTop}>
          <Typography variant="h5" className={classes.title}>Checklists</Typography>
          <Box className={classes.headerActions}>
            <Box className={classes.modeToggle}>
              <button
                type="button"
                className={`${classes.modeBtn}${mode === 'board' ? ` ${classes.modeBtnActive}` : ''}`}
                onClick={() => setMode('board')}
              >
                Checklist
              </button>
              <button
                type="button"
                className={`${classes.modeBtn}${mode === 'history' ? ` ${classes.modeBtnActive}` : ''}`}
                onClick={() => setMode('history')}
              >
                History
              </button>
            </Box>
            {mode === 'board' && (
              <>
                <FormControl>
                  <Select
                    className={classes.centreSelect}
                    value={centreUuid}
                    onChange={(e) => pickCentre(e.target.value)}
                  >
                    {centres.map((c) => (
                      <MenuItem key={c.uuid} value={c.uuid}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconButton size="small" className={classes.refreshBtn} onClick={loadBoard} disabled={loading}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </Box>
        </Box>

        {mode === 'board' && hasAnyCards && (
          <Box className={classes.daystrip}>
            <div className={`${classes.ring} ${classes.ringBig}`} style={ringStyle(summary.pct, summary.done === summary.total ? 'done' : 'prog')}>
              <span className={classes.rp}>{summary.pct}%</span>
            </div>
            <Box>
              <Typography className={classes.dsBig}>{summary.done} of {summary.total} done</Typography>
              <Typography className={classes.dsSub}>across today, this week &amp; this month</Typography>
            </Box>
          </Box>
        )}
      </Box>

      {mode === 'history' && <ChecklistHistory initialCentre={centreUuid} />}

      {mode === 'board' && loading && (
        <Box className={classes.loadingWrap}><CircularProgress /></Box>
      )}

      {mode === 'board' && !loading && !hasAnyCards && (
        <Typography className={classes.empty}>No checklists for this centre yet.</Typography>
      )}

      {mode === 'board' && !loading && hasAnyCards && GROUPS.map(({ key, title }, gi) =>
        sections[key].length ? (
          <Box key={key}>
            <Typography className={`${classes.groupLabel}${gi === 0 ? ` ${classes.firstGroupLabel}` : ''}`}>
              {title}
            </Typography>
            {sections[key].map(renderRunCard)}
          </Box>
        ) : null
      )}

      <Dialog
        open={!!selected}
        onClose={closeRun}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: classes.dialogPaper }}
      >
        <Box className={classes.runHead}>
          <Box className={classes.runHeadTop}>
            <IconButton className={classes.backBtn} onClick={closeRun} aria-label="Back">
              <ArrowBackIcon className={classes.backIcon} />
            </IconButton>
            <Box className={classes.runHeadText}>
              <Typography className={classes.runName}>
                {selected && selected.template ? selected.template.name : 'Checklist'}
              </Typography>
              <Typography className={classes.runSub}>
                {selected && selected.centre ? `${selected.centre.name} · ` : ''}
                {liveDone} of {liveTotal} done
              </Typography>
            </Box>
            {!detailLoading && (
              <div className={classes.ring} style={ringStyle(isComplete ? 100 : livePct, isComplete ? 'done' : 'prog')}>
                <span className={classes.rp} style={isComplete ? { color: theme.palette.text.success } : {}}>
                  {isComplete ? '✓' : `${livePct}%`}
                </span>
              </div>
            )}
          </Box>
          <div className={classes.progressBar}>
            <div className={classes.progressFill} style={{ width: `${isComplete ? 100 : livePct}%` }} />
          </div>
        </Box>

        <DialogContent className={classes.runContent}>
          {detailLoading && (
            <Box textAlign="center" py={4}><CircularProgress /></Box>
          )}

          {!detailLoading && itemsByArea.map(({ area, items }) => {
            const areaDone = items.filter((ri) => (itemState[ri.uuid] || {}).answer).length
            return (
              <Box key={area}>
                <Box className={classes.areaHead}>
                  <span className={classes.areaName}>{area}</span>
                  <span className={classes.areaCount}>{areaDone}/{items.length}</span>
                </Box>
                {items.map((ri) => {
                  const s = itemState[ri.uuid] || {}
                  const captureType = (ri.item && ri.item.capture_type) || 'checkbox'
                  const requiresPhoto = captureType === 'photo' || (ri.item && ri.item.requires_photo)
                  const isData = captureType === 'data'
                  const dataKind = ri.item && ri.item.data_kind
                  const inputType = dataKind === 'number' ? 'number' : dataKind === 'date' ? 'date' : 'text'
                  return (
                    <Box
                      key={ri.uuid}
                      className={`${classes.check}${s.answer === 'yes' ? ` ${classes.checkDone}` : ''}${s.answer === 'no' ? ` ${classes.checkNo}` : ''}`}
                    >
                      <Box className={classes.cBody}>
                        <Box className={classes.cRow}>
                          <Typography className={classes.cText}>
                            {ri.item ? ri.item.text : ''}
                          </Typography>
                          <Box className={classes.yn} role="group" aria-label="Answer">
                            {['yes', 'no'].map((v) => (
                              <button
                                key={v}
                                type="button"
                                disabled={isComplete}
                                aria-pressed={s.answer === v}
                                className={`${classes.ynBtn}${s.answer === v ? ` ${v === 'yes' ? classes.ynYes : classes.ynNo}` : ''}`}
                                onClick={() => !isComplete && setItem(ri.uuid, { answer: s.answer === v ? '' : v })}
                              >
                                {v === 'yes' ? 'Yes' : 'No'}
                              </button>
                            ))}
                          </Box>
                        </Box>

                        {s.answer === 'no' && (
                          <Box className={`${classes.stack} ${classes.stackFull}`}>
                            <Typography className={classes.noteLabel}>What is not working? (required)</Typography>
                            {isComplete ? (
                              <Typography className={classes.noteText}>{s.note}</Typography>
                            ) : (
                              <TextField
                                className={classes.noteInput}
                                size="small"
                                variant="outlined"
                                multiline
                                minRows={2}
                                value={s.note || ''}
                                placeholder="Describe the problem, then raise an Issue Tracker ticket so it gets resolved"
                                onChange={(e) => setItem(ri.uuid, { note: e.target.value })}
                              />
                            )}
                          </Box>
                        )}

                        {(requiresPhoto || isData) && (
                          <Box className={classes.stack}>
                            {requiresPhoto && (
                              <span className={`${classes.tag} ${classes.tagPhoto}`}>📷 Photo required</span>
                            )}
                            {isData && (
                              <span className={`${classes.tag} ${classes.tagData}`}>🔢 Reading required</span>
                            )}

                            {requiresPhoto && s.photoUrl && (
                              <img src={s.photoUrl} alt="evidence" className={classes.thumb} />
                            )}
                            {requiresPhoto && !isComplete && (
                              <Button component="label" size="small" startIcon={<PhotoCamera />} disabled={s.uploading}>
                                {s.uploading ? 'Uploading…' : s.photoUrl ? 'Retake' : 'Take photo'}
                                <input
                                  hidden
                                  type="file"
                                  accept="image/*"
                                  capture="environment"
                                  onChange={(e) => onPhotoSelected(ri.uuid, e.target.files && e.target.files[0])}
                                />
                              </Button>
                            )}

                            {isData && (
                              <TextField
                                className={classes.dataInput}
                                size="small"
                                variant="outlined"
                                fullWidth
                                type={inputType}
                                disabled={isComplete}
                                value={s.value || ''}
                                placeholder={dataKind === 'number' ? 'Enter reading' : 'Enter value'}
                                InputLabelProps={{ shrink: true }}
                                onChange={(e) => setItem(ri.uuid, { value: e.target.value })}
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            )
          })}
        </DialogContent>

        <Box className={classes.footer}>
          {!isComplete && missingRequired.length > 0 && (
            <Typography className={classes.hint}>
              <span className={classes.hintWarn}>{missingRequired.length}</span> of {selected && selected.run_items ? selected.run_items.length : 0} checkpoint{missingRequired.length > 1 ? 's' : ''} still pending — all are required
            </Typography>
          )}
          {!isComplete && noWithoutNote > 0 && (
            <Typography className={classes.hint}>
              <span className={classes.hintWarn}>{noWithoutNote}</span> No answer{noWithoutNote > 1 ? 's' : ''} without a note
            </Typography>
          )}
          {isComplete ? (
            <Button onClick={closeRun} variant="outlined" className={classes.submitBtn}>Close</Button>
          ) : (
            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              className={classes.submitBtn}
              disabled={submitting || detailLoading || missingRequired.length > 0 || noWithoutNote > 0}
            >
              {submitting ? 'Submitting…'
                : problemCount ? `Submit with ${problemCount} problem${problemCount > 1 ? 's' : ''}` : 'Submit checklist'}
            </Button>
          )}
        </Box>
      </Dialog>
    </Box>
  )
}

export default Checklists
